import { useTranslations as useTranslations$1 } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { LocaleSwitcher } from '@/components/i18n/LocaleSwitcher';
import { DiscordIcon } from '@/components/icons';
import { DISCORD_INVITE_URL } from '@/lib/constants';
import { DOCS_HOME } from '@/lib/docs';
import { MCP_CLIENT_ROUTE_BASE } from '@/lib/mcpDocsClients';
import { TrackedDiscordLink } from '@/components/analytics/TrackedDiscordLink';
interface FooterLink {
    label: string;
    href: string;
    external?: boolean;
}
interface SocialLink {
    label: string;
    href: string;
    icon: 'instagram' | 'linkedin' | 'discord';
}
function InstagramIcon({ className }: {
    className?: string;
}) {
    return (<svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>);
}
function LinkedInIcon({ className }: {
    className?: string;
}) {
    return (<svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>);
}
const socialIcons = {
    instagram: InstagramIcon,
    linkedin: LinkedInIcon,
    discord: DiscordIcon
} as const;
const FOOTER_LINK_CLASS = 'group flex items-center gap-2 text-sm text-zinc-400 transition-all duration-200 hover:text-white';
function FooterNavLink({ link }: {
    link: FooterLink;
}) {
    const label = <span className="transition-transform duration-200 group-hover:translate-x-1">{link.label}</span>;
    return link.external ? (<a href={link.href} className={FOOTER_LINK_CLASS}>
      {label}
    </a>) : (<Link href={link.href} className={FOOTER_LINK_CLASS}>
      {label}
    </Link>);
}
export function Footer() {
    const t = useTranslations$1();
    const footerLinks: {
        company: FooterLink[];
        about: FooterLink[];
        legal: FooterLink[];
        social: SocialLink[];
    } = {
        // Docs, the Blender skill, and the MCP connector live on the (app) Learn
        // surface, which is public but not locale-prefixed — `external` renders a
        // plain <a> so the locale-aware Link can't send a non-English visitor to
        // /<locale>/docs (a redirect hop at best).
        company: [
            {
                label: t("5Wwopw"),
                href: '/pricing'
            },
            {
                label: t("xvs3an"),
                href: '/supported-models'
            },
            {
                label: 'Blender Skill',
                href: '/skills/blender',
                external: true
            },
            {
                label: 'MCP Connector',
                href: MCP_CLIENT_ROUTE_BASE,
                external: true
            },
            {
                label: t("UYU6ck"),
                href: '/download'
            }
        ],
        about: [
            {
                label: t("_6_sPX"),
                href: DOCS_HOME,
                external: true
            },
            {
                label: t("wB_wXs"),
                href: '/credits'
            },
            {
                label: t("HqRNN8"),
                href: '/support'
            },
            {
                label: t("zFegDD"),
                href: 'mailto:koh@c47.studio',
                external: true
            }
        ],
        legal: [
            {
                label: t("32rBNK"),
                href: '/terms'
            },
            {
                label: t("vx0nkZ"),
                href: '/privacy'
            },
            {
                label: t("Py189G"),
                href: '/security'
            }
        ],
        social: [
            {
                // i18n-ignore -- brand name
                label: 'Discord',
                href: DISCORD_INVITE_URL,
                icon: 'discord'
            },
            {
                label: 'Instagram',
                href: 'https://www.instagram.com/martini.film/',
                icon: 'instagram'
            },
            {
                label: 'LinkedIn',
                href: 'https://www.linkedin.com/company/martini-film/',
                icon: 'linkedin'
            }
        ]
    };
    return (<footer className="relative border-t border-zinc-800/30 bg-black">
      { /* Subtle gradient overlay - hidden on mobile to prevent visual artifacts */ }
      <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-t from-zinc-900/10 via-transparent to-transparent sm:block"/>

      <div className="relative mx-auto max-w-5xl px-6 py-12 sm:px-8 lg:px-12 lg:py-16">
        <div className="mb-12 flex flex-wrap justify-start gap-8 md:justify-center lg:gap-16">
          { /* Company Section */ }
          <div className="flex-shrink-0">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">{t("9YazHG")}</h3>
            <nav className="flex flex-col gap-3" aria-label="Company links">
              {footerLinks.company.map((link)=>(<FooterNavLink key={link.href} link={link}/>))}
            </nav>
          </div>

          { /* About Section */ }
          <div className="flex-shrink-0">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">{t("g5pX-a")}</h3>
            <nav className="flex flex-col gap-3" aria-label="About links">
              {footerLinks.about.map((link)=>(<FooterNavLink key={link.href} link={link}/>))}
            </nav>
          </div>

          { /* Legal Section */ }
          <div className="flex-shrink-0">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">{t("7oFrM6")}</h3>
            <nav className="flex flex-col gap-3" aria-label="Legal links">
              {footerLinks.legal.map((link)=>(<FooterNavLink key={link.href} link={link}/>))}
            </nav>
          </div>

          { /* Connect Section */ }
          <div className="flex-shrink-0">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">{t("-vVZ_G")}</h3>
            <div className="flex flex-col gap-3">
              {footerLinks.social.map((link)=>{
        const Icon = socialIcons[link.icon];
        const content = (<>
                    <div className="flex h-5 w-5 items-center justify-center rounded-md bg-zinc-800/50 transition-all duration-200 group-hover:scale-110 group-hover:bg-zinc-700/50">
                      <Icon className="h-3.5 w-3.5"/>
                    </div>
                    <span className="transition-transform duration-200 group-hover:translate-x-1">{link.label}</span>
                  </>);
        if (link.icon === 'discord') {
            return (<TrackedDiscordLink key={link.href} source="marketing_footer" className="group flex items-center gap-3 text-sm text-zinc-400 transition-all duration-200 hover:text-white">
                      {content}
                    </TrackedDiscordLink>);
        }
        return (<a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 text-sm text-zinc-400 transition-all duration-200 hover:text-white">
                    {content}
                  </a>);
    })}
            </div>
          </div>
        </div>

        { /* Bottom Bar */ }
        <div className="border-t border-zinc-800/30 pt-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-xs tracking-wide text-zinc-500 sm:text-sm">
              {t("bFPx9p", {
        year: String(new Date().getFullYear())
    })}
            </p>
            <LocaleSwitcher variant="dropdown" className="w-full max-w-56 sm:w-48"/>
          </div>
        </div>
      </div>
    </footer>);
}
